import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthorPanel from "@/pages/dashboard/AuthorPanel";
import ReviewerPanel from "@/pages/dashboard/ReviewerPanel";
import EditorialPanel from "@/pages/dashboard/EditorialPanel";
import ContentAdmin from "@/pages/dashboard/ContentAdmin";
import IssuesAdmin from "@/pages/dashboard/IssuesAdmin";
import UsersAdmin from "@/pages/dashboard/UsersAdmin";
import SettingsAdmin from "@/pages/dashboard/SettingsAdmin";
import PagesAdmin from "@/pages/dashboard/PagesAdmin";
import HomepageAdmin from "@/pages/dashboard/HomepageAdmin";
import ContactInbox from "@/pages/dashboard/ContactInbox";
import OutreachAdmin from "@/pages/dashboard/OutreachAdmin";
import { Button } from "@/components/ui/button";
import {
  Compass, FileText, ClipboardCheck, Gavel, BookOpen, Users, Settings,
  LogOut, Home, UploadCloud, Menu, X, Layers, ScrollText, Image as ImageIcon, Inbox, Bot,
} from "lucide-react";

const NAV = {
  author: [{ k: "submissions", l: "My Submissions", icon: FileText, C: AuthorPanel }],
  reviewer: [{ k: "reviews", l: "Review Queue", icon: ClipboardCheck, C: ReviewerPanel }],
  editor: [
    { k: "editorial", l: "Editorial Queue", icon: Gavel, C: EditorialPanel },
    { k: "content", l: "Content", icon: BookOpen, C: ContentAdmin },
    { k: "issues", l: "Issue Manager", icon: Layers, C: IssuesAdmin },
  ],
  admin: [
    { k: "editorial", l: "Editorial Queue", icon: Gavel, C: EditorialPanel },
    { k: "content", l: "Content", icon: BookOpen, C: ContentAdmin },
    { k: "issues", l: "Issue Manager", icon: Layers, C: IssuesAdmin },
    { k: "homepage", l: "Homepage", icon: ImageIcon, C: HomepageAdmin },
    { k: "pages", l: "Pages", icon: ScrollText, C: PagesAdmin },
    { k: "inbox", l: "Inbox", icon: Inbox, C: ContactInbox },
    { k: "users", l: "Users & Roles", icon: Users, C: UsersAdmin },
    { k: "outreach", l: "AI Outreach", icon: Bot, C: OutreachAdmin },
    { k: "settings", l: "Site Settings", icon: Settings, C: SettingsAdmin },
  ],
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = NAV[user?.role] || NAV.author;
  const [active, setActive] = useState(items[0].k);
  const Active = (items.find((i) => i.k === active) || items[0]).C;

  const logoutGo = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-primary text-white flex flex-col transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/noventra-logo.png" alt="Noventra Academic Press" className="w-9 h-9 rounded-lg object-cover bg-white" />
            <span className="font-serif font-bold">Noventra</span>
          </Link>
          <button className="lg:hidden" onClick={() => setMobileOpen(false)}><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 pb-4">
          <p className="text-sm font-medium truncate">{user?.name}</p>
          <p className="text-xs text-amber-300 capitalize">{user?.role}</p>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {items.map((i) => (
            <button
              key={i.k}
              onClick={() => { setActive(i.k); setMobileOpen(false); }}
              data-testid={`dash-nav-${i.k}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active === i.k ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/5"}`}
            >
              <i.icon className="w-4 h-4" /> {i.l}
            </button>
          ))}
          {user?.role === "author" && (
            <Link to="/submit" data-testid="dash-nav-submit" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-white/5">
              <UploadCloud className="w-4 h-4" /> New Submission
            </Link>
          )}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-1">
          <Link to="/" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/5"><Home className="w-4 h-4" /> Back to site</Link>
          <button onClick={logoutGo} data-testid="dash-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/5"><LogOut className="w-4 h-4" /> Log out</button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="lg:hidden flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="dash-mobile-toggle"><Menu className="w-5 h-5" /></Button>
          <span className="font-serif font-bold">Dashboard</span>
        </div>
        <div className="p-6 md:p-10 max-w-6xl">
          <Active />
        </div>
      </div>
    </div>
  );
}
